import { useCurrentUser } from '@/auth/useAuth';
import { withForm } from '@/components/form';
import { SectionCard } from '@/components/ui';

import { defaultFormValues, noteCategoryEnum } from '../../contactSchema';
import { AddItemButton, RepeaterItem } from './RepeaterItem';

const titleCase = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

// "search" notes are written read-only by the auto-investigation job and rendered in
// their own section below; users never pick that category from the editable form.
const CATEGORY_OPTIONS = [
  { value: '', label: '—' },
  ...noteCategoryEnum.options
    .filter((v) => v !== '' && v !== 'search')
    .map((v) => ({ value: v, label: titleCase(v) })),
];

interface BraveResult {
  title?: string;
  url?: string;
  description?: string;
  meta_url?: { hostname?: string };
  profile?: { name?: string; long_name?: string };
}

function parseSearchResults(text: string): BraveResult[] {
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function SearchResultsCard({
  note,
}: {
  note: { text: string; addedAt?: string; addedBy?: string };
}) {
  const results = parseSearchResults(note.text);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
        {note.addedBy ? `By ${note.addedBy}` : 'Auto investigation'}
        {note.addedAt && ` · ${new Date(note.addedAt).toLocaleString()}`}
      </div>
      {results.length === 0 ? (
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No results found.</span>
      ) : (
        results.map((r, i) => {
          const source = r.profile?.long_name || r.meta_url?.hostname || r.profile?.name;
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {r.url ? (
                <a href={r.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600 }}>
                  {r.title || r.url}
                </a>
              ) : (
                <span style={{ fontSize: 13, fontWeight: 600 }}>{r.title}</span>
              )}
              {source && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{source}</span>}
              {r.description && (
                <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.description}</span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

export const NotesSection = withForm({
  defaultValues: defaultFormValues,
  render: function NotesSection({ form }) {
    const user = useCurrentUser();
    const addedBy = user?.username || user?.email || '';

    const handleAdd = () => {
      form.pushFieldValue('notes', {
        text: '',
        category: '',
        addedAt: new Date().toISOString(),
        addedBy,
      });
    };

    return (
      <form.AppField name="notes">
        {(field) => {
          const notes = field.state.value ?? [];
          const searchNotes = notes.filter((n) => n.category === 'search');
          return (
            <>
              {searchNotes.length > 0 && (
                <SectionCard title="Search">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {searchNotes.map((note, i) => (
                      <SearchResultsCard key={i} note={note} />
                    ))}
                  </div>
                </SectionCard>
              )}

              <SectionCard title="Notes" actions={<AddItemButton onClick={handleAdd} />}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {notes.map((note, i) =>
                    note.category === 'search' ? null : (
                      <RepeaterItem key={i} onRemove={() => field.removeValue(i)} removeLabel="Remove note">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
                            <form.AppField name={`notes[${i}].category`}>
                              {(f) => (
                                <div style={{ width: 200 }}>
                                  <f.Select label="Category" options={CATEGORY_OPTIONS} />
                                </div>
                              )}
                            </form.AppField>
                            {note.addedBy && (
                              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                                By {note.addedBy}
                                {note.addedAt && ` · ${new Date(note.addedAt).toLocaleString()}`}
                              </span>
                            )}
                          </div>
                          <form.AppField name={`notes[${i}].text`}>
                            {(f) => <f.TextField label="Note text" required multiline rows={3} />}
                          </form.AppField>
                        </div>
                      </RepeaterItem>
                    ),
                  )}
                </div>
              </SectionCard>
            </>
          );
        }}
      </form.AppField>
    );
  },
});
